import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Hash, ChevronLeft, Compass, AlertTriangle, Star, Sparkles, Loader2, Heart, Brain, TrendingUp, Shield } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KarmaCard = ({ title, icon: Icon, children, variant = 'default' }) => {
  const variants = {
    default: 'bg-white border-gray-200',
    warning: 'bg-amber-50 border-amber-200',
    success: 'bg-emerald-50 border-emerald-200',
    info: 'bg-blue-50 border-blue-200'
  };
  
  return (
    <div className={`p-6 rounded-2xl border-2 ${variants[variant]}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          variant === 'warning' ? 'bg-amber-500' : 
          variant === 'success' ? 'bg-emerald-500' : 
          variant === 'info' ? 'bg-blue-500' : 'bg-gray-200'
        }`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h3 className="font-heading text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      {children}
    </div>
  );
};

const NumerologiaKarmaPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: ''
  });
  
  const [karma, setKarma] = useState(null);
  const [loading, setLoading] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Ajoutez votre nom de famille pour calculer'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.birthDate) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/karma/analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          birth_date: formData.birthDate
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setKarma(data.karma_analysis);
        toast.success('Analyse karmique terminée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getAIInterpretation = async () => {
    if (!karma) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/karma/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          karma_data: karma,
          first_name: formData.firstName || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation karmique générée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/numerologia')}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-emerald-600" />
            <span className="font-heading text-lg font-bold text-emerald-700">
              Karma & Évolution
            </span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Form */}
          {!karma && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-md mx-auto"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Compass className="h-8 w-8 text-white" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  Analyse karmique
                </h1>
                <p className="text-gray-600">
                  Découvrez vos leçons de vie et votre chemin d'évolution
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">Prénom</Label>
                    <Input
                      id="firstName"
                      data-testid="karma-firstname"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      placeholder="Marie"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Nom</Label>
                    <Input
                      id="lastName"
                      data-testid="karma-lastname"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      placeholder="Dupont"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="birthDate">Date de naissance</Label>
                  <Input
                    id="birthDate"
                    data-testid="karma-birthdate"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <Button
                  type="submit"
                  data-testid="calculate-karma-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-6"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Compass className="mr-2 h-5 w-5" />
                      Analyser mon karma
                    </>
                  )}
                </Button>
              </form>
              
              {/* Disclaimer */}
              <p className="text-xs text-gray-500 text-center mt-6">
                L'analyse karmique est un outil de réflexion, pas une prédiction.
                Elle vise à éclairer votre chemin d'évolution.
              </p>
            </motion.div>
          )}

          {/* Results */}
          {karma && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  Votre analyse karmique
                </h1>
                <p className="text-gray-600">
                  Chemin de vie {karma.life_path?.number}
                </p>
              </div>

              {/* Karmic Debts */}
              {karma.karmic_debts?.length > 0 && (
                <KarmaCard title="Dettes karmiques" icon={AlertTriangle} variant="warning">
                  <div className="space-y-4">
                    {karma.karmic_debts.map((debt, i) => (
                      <div key={i}>
                        <h4 className="font-semibold text-amber-800 mb-1">{debt.lesson}</h4>
                        <p className="text-amber-700 text-sm">{debt.description}</p>
                        <p className="text-amber-600 text-sm mt-2 italic">{debt.healing}</p>
                      </div>
                    ))}
                  </div>
                </KarmaCard>
              )}

              {/* Missing Numbers (Karmic Lessons) */}
              {karma.karmic_lessons?.length > 0 && (
                <KarmaCard title="Leçons à apprendre" icon={Brain} variant="info">
                  <p className="text-blue-700 text-sm mb-4">
                    Ces nombres sont absents de votre nom, indiquant des qualités à développer.
                  </p>
                  <div className="space-y-3">
                    {karma.karmic_lessons.map((lesson, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-white/50 rounded-xl">
                        <span className="w-8 h-8 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold flex-shrink-0">
                          {lesson.number}
                        </span>
                        <div>
                          <p className="font-medium text-blue-800">{lesson.lesson}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {lesson.keywords?.slice(0, 3).map((kw, j) => (
                              <span key={j} className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </KarmaCard>
              )}

              {/* Strengths (Dominant Numbers) */}
              {karma.strengths?.length > 0 && (
                <KarmaCard title="Vos forces dominantes" icon={Star} variant="success">
                  <p className="text-emerald-700 text-sm mb-4">
                    Ces nombres apparaissent fréquemment dans votre nom, indiquant vos talents naturels.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {karma.strengths.map((strength, i) => (
                      <div key={i} className="p-3 bg-white/50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-8 h-8 bg-emerald-500 text-white rounded-lg flex items-center justify-center font-bold">
                            {strength.number}
                          </span>
                          <span className="text-xs text-emerald-600">×{strength.occurrences}</span>
                        </div>
                        <p className="font-medium text-emerald-800 text-sm">{strength.strength}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {strength.keywords?.slice(0, 2).map((kw, j) => (
                            <span key={j} className="px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded-full text-xs">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </KarmaCard>
              )}

              {/* Evolution Advice */}
              <KarmaCard title="Conseil d'évolution" icon={TrendingUp}>
                <p className="text-gray-700">{karma.evolution_advice}</p>
              </KarmaCard>

              {/* AI Interpretation */}
              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  Interprétation approfondie
                </h3>
                
                {interpretation ? (
                  <div className="prose prose-sm max-w-none text-gray-700">
                    <ReactMarkdown>{interpretation}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Button
                      onClick={getAIInterpretation}
                      disabled={loadingInterpretation}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                      data-testid="get-karma-interpretation-btn"
                    >
                      {loadingInterpretation ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Analyse...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-5 w-5" />
                          Obtenir l'interprétation IA
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Positive Note */}
              <div className="p-6 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-2xl text-center">
                <Shield className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                <h3 className="font-heading text-lg font-semibold text-emerald-800 mb-2">
                  Rappel important
                </h3>
                <p className="text-emerald-700 text-sm max-w-md mx-auto">
                  Le karma n'est pas une punition, mais une opportunité d'évolution.
                  Chaque défi est une invitation à grandir.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => setKarma(null)}
                  className="rounded-xl"
                >
                  Nouvelle analyse
                </Button>
                <Button
                  onClick={() => navigate('/numerologia/theme')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Voir mon thème complet
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumerologiaKarmaPage;
